// Templates list (ADMIN_UI §3.5). Filter by campaign / published. New requires
// picking a campaign first (per spec) — handled by /templates/new.

import { useQuery } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { AsyncState } from '../../components/AsyncState.js';
import { Badge } from '../../components/ui/badge.js';
import { Button } from '../../components/ui/button.js';
import { Select } from '../../components/ui/select.js';
import { Table, TBody, TD, TH, THead, TR } from '../../components/ui/table.js';
import { listCampaigns } from '../../lib/api/campaigns.js';
import { listTemplates } from '../../lib/api/templates.js';
import { languageLabel } from '../../lib/languages.js';
import { qk } from '../../lib/query-keys.js';

export const Route = createFileRoute('/_authed/templates/')({
  component: TemplatesListPage,
});

function TemplatesListPage() {
  const [published, setPublished] = useState<'' | 'true' | 'false'>('');
  const [campaignId, setCampaignId] = useState<string>('');
  const queryKey = qk.templates.list({ published, campaignId });
  const query = useQuery({
    queryKey,
    queryFn: () =>
      listTemplates({
        ...(published === '' ? {} : { published: published === 'true' }),
        ...(campaignId === '' ? {} : { campaign_id: Number(campaignId) }),
      }),
  });
  const campaignsQuery = useQuery({
    queryKey: qk.campaigns.list({}),
    queryFn: () => listCampaigns(),
  });

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-xl font-semibold text-neutral-900">Templates</h2>
        <Link to="/templates/new">
          <Button>New template</Button>
        </Link>
      </div>

      <p className="mb-4 text-sm text-neutral-500">
        To edit a template, click its ID in the first column.
      </p>

      <div className="mb-4 flex gap-2">
        <Select
          aria-label="Filter by campaign"
          value={campaignId}
          onChange={(e) => setCampaignId(e.target.value)}
        >
          <option value="">All campaigns</option>
          {campaignsQuery.data?.campaigns.map((c) => (
            <option key={c.id} value={c.id}>
              {c.campaign_key}
            </option>
          ))}
        </Select>
        <Select
          aria-label="Filter by published"
          value={published}
          onChange={(e) => setPublished(e.target.value as typeof published)}
        >
          <option value="">All publish states</option>
          <option value="true">Published</option>
          <option value="false">Draft</option>
        </Select>
      </div>

      <AsyncState
        query={query}
        isEmpty={(d) => d.templates.length === 0}
        empty={
          <p className="text-sm text-neutral-500">
            No templates yet.{' '}
            <Link to="/templates/new" className="text-neutral-900 underline">
              Create one
            </Link>
            .
          </p>
        }
      >
        {(data) => (
          <Table>
            <THead>
              <TR>
                <TH>ID</TH>
                <TH>Campaign</TH>
                <TH>Language</TH>
                <TH>Subject</TH>
                <TH>Status</TH>
                <TH>Updated</TH>
              </TR>
            </THead>
            <TBody>
              {data.templates.map((t) => (
                <TR key={t.id}>
                  <TD className="font-mono text-xs">
                    <Link
                      to="/templates/$templateId"
                      params={{ templateId: t.id }}
                      className="hover:underline"
                    >
                      {t.id}
                    </Link>
                  </TD>
                  <TD className="text-xs">
                    <Link
                      to="/campaigns/$campaignId"
                      params={{ campaignId: String(t.campaign_id) }}
                      className="text-neutral-900 hover:underline"
                      title={t.campaign_name}
                    >
                      <span className="font-mono">{t.campaign_key}</span>
                    </Link>
                  </TD>
                  <TD className="text-xs">{languageLabel(t.language)}</TD>
                  <TD className="max-w-sm truncate">{t.subject}</TD>
                  <TD>
                    {t.sparkpost_published ? (
                      <Badge variant="success">published</Badge>
                    ) : (
                      <Badge variant="neutral">draft</Badge>
                    )}
                  </TD>
                  <TD className="text-xs text-neutral-500">
                    {new Date(t.updated_at).toLocaleString()}
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
        )}
      </AsyncState>
    </div>
  );
}
